import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';

interface UsageSummary {
  period: string;
  storeId: string;
  sessions: {
    total: number;
    voice: number;
    takeback: number;
    browse: number;
    uniqueStores: number;
    uniqueCustomers: number;
  };
  messages: {
    total: number;
    user: number;
    assistant: number;
  };
  aiUsage: {
    requests: number;
    totalMinutes: string;
    totalTokens: number;
    avgLatencyMs: number;
  };
  billing: {
    aiMinutes: number;
    ratePerMinute: number;
    totalCost: string;
  };
  perStore: Array<{
    store_id: string;
    sessions: number;
    messages: number;
    ai_minutes: number;
  }>;
  generatedAt: string;
}

export const Route = createFileRoute('/pin_/usage')({
  component: PINUsagePage,
});

function PINUsagePage() {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [storeFilter] = useState('');

  useEffect(() => {
    loadUsage();
  }, [period, storeFilter]);

  const loadUsage = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (storeFilter) params.append('storeId', storeFilter);
      
      const response = await fetch(`/api/v1/pin/usage?${params}`);
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Failed to load usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams({ period, format: 'csv' });
    if (storeFilter) params.append('storeId', storeFilter);
    
    window.open(`/api/v1/pin/usage?${params}`, '_blank');
  };

  const storeNames: Record<string, string> = {
    'lululemon-short-hills': 'Short Hills Mall',
    'lululemon-5th-ave': 'Fifth Avenue Flagship',
    'lululemon-santa-monica': 'Santa Monica',
    'short-hills-nj': 'Short Hills Mall',
  };

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/pin" className="text-sm flex items-center gap-1 mb-4" style={{ color: '#A9A9A9' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 18L9 12L15 6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Physical Intelligence
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-light mb-1" style={{ color: '#171717' }}>
              AI Educator Usage
            </h1>
            <p className="text-sm" style={{ color: '#A9A9A9' }}>
              Track sessions, AI minutes, and billing metrics
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-2 border text-sm"
              style={{ borderColor: 'rgba(169,169,169,0.3)' }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
            
            {/* Export button */}
            <button
              onClick={exportCsv}
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: '#FAFAFA', color: '#171717', border: '1px solid rgba(169,169,169,0.3)' }}
            >
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: '#00FBA9' }} />
        </div>
      ) : usage ? (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Sessions" value={usage.sessions.total} />
            <StatCard label="Voice Sessions" value={usage.sessions.voice} />
            <StatCard label="Total Messages" value={usage.messages.total} />
            <StatCard label="Unique Customers" value={usage.sessions.uniqueCustomers} />
          </div>

          {/* AI Usage Stats */}
          <div className="mb-8">
            <h2 className="text-xs font-medium tracking-wider mb-4" style={{ color: '#A9A9A9' }}>AI USAGE</h2>
            <div className="grid grid-cols-4 gap-4">
              <StatCard label="AI Requests" value={usage.aiUsage.requests} />
              <StatCard 
                label="AI Minutes" 
                value={parseFloat(usage.aiUsage.totalMinutes).toFixed(2)}
                suffix="min"
              />
              <StatCard 
                label="Total Tokens" 
                value={usage.aiUsage.totalTokens.toLocaleString()}
              />
              <StatCard 
                label="Avg Latency" 
                value={usage.aiUsage.avgLatencyMs}
                suffix="ms"
              />
            </div>
          </div>

          {/* Billing */}
          <div className="mb-8">
            <h2 className="text-xs font-medium tracking-wider mb-4" style={{ color: '#A9A9A9' }}>BILLING ESTIMATE</h2>
            <div className="bg-white border p-6" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <div className="grid grid-cols-3 gap-8">
                <div>
                  <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>AI Minutes Used</p>
                  <p className="text-2xl font-light" style={{ color: '#171717' }}>
                    {parseFloat(usage.aiUsage.totalMinutes).toFixed(2)} <span className="text-sm" style={{ color: '#A9A9A9' }}>min</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Rate</p>
                  <p className="text-2xl font-light" style={{ color: '#171717' }}>
                    ${usage.billing.ratePerMinute} <span className="text-sm" style={{ color: '#A9A9A9' }}>/min</span>
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#A9A9A9' }}>Estimated Cost</p>
                  <p className="text-2xl font-light" style={{ color: '#00FBA9' }}>
                    ${usage.billing.totalCost}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Per-Store Breakdown */}
          <div>
            <h2 className="text-xs font-medium tracking-wider mb-4" style={{ color: '#A9A9A9' }}>PER-STORE BREAKDOWN</h2>
            <div className="bg-white border overflow-hidden" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'rgba(169,169,169,0.1)' }}>
                    <th className="text-left text-xs font-medium px-6 py-3" style={{ color: '#A9A9A9' }}>STORE</th>
                    <th className="text-right text-xs font-medium px-4 py-3" style={{ color: '#A9A9A9' }}>SESSIONS</th>
                    <th className="text-right text-xs font-medium px-4 py-3" style={{ color: '#A9A9A9' }}>MESSAGES</th>
                    <th className="text-right text-xs font-medium px-6 py-3" style={{ color: '#A9A9A9' }}>AI MINUTES</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.perStore.length > 0 ? usage.perStore.map((store) => (
                    <tr key={store.store_id} className="border-b" style={{ borderColor: 'rgba(169,169,169,0.05)' }}>
                      <td className="px-6 py-3">
                        <p className="text-sm font-medium" style={{ color: '#171717' }}>
                          {storeNames[store.store_id] || store.store_id}
                        </p>
                        <p className="text-xs" style={{ color: '#A9A9A9' }}>{store.store_id}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: '#171717' }}>{store.sessions}</td>
                      <td className="px-4 py-3 text-sm text-right" style={{ color: '#171717' }}>{store.messages}</td>
                      <td className="px-6 py-3 text-sm text-right font-medium" style={{ color: '#00FBA9' }}>
                        {(store.ai_minutes || 0).toFixed(4)}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-sm" style={{ color: '#A9A9A9' }}>
                        No usage data for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Generated timestamp */}
          <p className="text-xs mt-6 text-center" style={{ color: '#A9A9A9' }}>
            Generated: {new Date(usage.generatedAt).toLocaleString()}
          </p>
        </>
      ) : (
        <div className="p-12 text-center">
          <p style={{ color: '#A9A9A9' }}>No usage data available</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ 
  label, 
  value, 
  suffix 
}: { 
  label: string; 
  value: string | number; 
  suffix?: string;
}) {
  return (
    <div className="bg-white border p-4" style={{ borderColor: 'rgba(169,169,169,0.2)' }}>
      <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#A9A9A9' }}>{label}</p>
      <p className="text-2xl font-light" style={{ color: '#171717' }}>
        {value}
        {suffix && <span className="text-sm ml-1" style={{ color: '#A9A9A9' }}>{suffix}</span>}
      </p>
    </div>
  );
}

